/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Row,
  Col,
  Badge,
  Alert,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import PerformanceFeedback from '../../../../services/PerformanceFeedback';
import PerformanceOneOnOne from '../../../../services/PerformanceOneOnOne';
import PerformanceImprovement from '../../../../services/PerformanceImprovement';
import PerformanceVerbalWarning from '../../../../services/PerformanceVerbalWarning';
import PerformanceDisciplinaryAction from '../../../../services/PerformanceDisciplinaryAction';
import { toast } from 'react-toastify';
import moment from 'moment';
import { generateFeedbackPdf } from '../../../../services/pdf/FeedbackPdfService';
import { generateOneOnOnePdf } from '../../../../services/pdf/OneOnOnePdfService';
import { generateImprovementPdf } from '../../../../services/pdf/ImprovementPdfService';
import { generateVerbalWarningPdf } from '../../../../services/pdf/VerbalWarningPdfService';
import { generateDisciplinaryActionPdf } from '../../../../services/pdf/DisciplinaryActionPdfService';
import OpacityLoaderSpinner from '../../../../components/Core/OpacityLoaderSpinner';
import AnnualReviewsSection from '../../AnnualReview/AnnualReviewsSection';

const extractPerformanceField = (data: any, candidates: string[]): string => {
  for (const key of candidates) {
    const segments = key.split('.');
    let current = data;
    for (const segment of segments) {
      if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
        current = current[segment];
      } else {
        current = undefined;
        break;
      }
    }
    if (current !== undefined && current !== null) {
      if (typeof current === 'string' && current.trim() !== '') {
        return current.trim();
      }
      if (typeof current === 'number') {
        return String(current);
      }
      if (current instanceof Date && !Number.isNaN(current.getTime())) {
        return moment(current).format('MM/DD/YY');
      }
    }
  }
  return '';
};

const getPolicyViolationText = (item: any): string => {
  if (item?.policyViolations && Array.isArray(item.policyViolations) && item.policyViolations.length > 0) {
    const joined = item.policyViolations
      .map((pv: any) => (typeof pv === 'string' ? pv : pv?.name ?? pv?.key ?? ''))
      .filter((p: string) => p && String(p).trim() !== '')
      .join(', ');
    if (joined) {
      return joined;
    }
  }
  return (
    extractPerformanceField(item, ['policyViolation.name', 'policyViolation', 'policy_violation', 'policy_violated']) ||
    ''
  );
};

const truncateCell = (text: string, maxLen: number) => {
  const t = text || '';
  if (t.length <= maxLen) {
    return t;
  }
  return `${t.substring(0, maxLen)}...`;
};

const joinDetailParts = (parts: (string | undefined)[]) =>
  parts
    .map((p) => (p && String(p).trim() !== '' ? String(p).trim() : ''))
    .filter(Boolean)
    .join('\n\n');

interface PerformanceManagementTabProps {
  userId: string;
}

const PerformanceManagementTab: React.FC<PerformanceManagementTabProps> = ({
  userId,
}: PerformanceManagementTabProps) => {
  const { token, user, currentSchool } = useContext(AuthContext);
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const [feedbackData, setFeedbackData] = useState<any[]>([]);
  const [oneOnOneData, setOneOnOneData] = useState<any[]>([]);
  const [performanceImprovementData, setPerformanceImprovementData] = useState<any[]>([]);
  const [verbalWarningData, setVerbalWarningData] = useState<any[]>([]);
  const [disciplinaryActionData, setDisciplinaryActionData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalType, setDetailModalType] = useState<string | null>(null);
  const [detailModalItem, setDetailModalItem] = useState<any>(null);

  const accordionItems = [
    {
      id: 'feedback',
      title: 'Feedback',
      icon: 'fas fa-comments',
      enabled: true,
    },
    {
      id: 'one-on-one',
      title: 'One on One',
      icon: 'fas fa-user-friends',
      enabled: true,
    },
    {
      id: 'performance-improvement',
      title: 'Performance Improvement',
      icon: 'fas fa-chart-line',
      enabled: true,
    },
    {
      id: 'verbal-warning',
      title: 'Verbal Warning',
      icon: 'fas fa-exclamation-triangle',
      enabled: true,
    },
    {
      id: 'disciplinary-action',
      title: 'Disciplinary Action Notice',
      icon: 'fas fa-gavel',
      enabled: true,
    },
  ];

  const columnsByType: { [key: string]: any[] } = {
    feedback: [
      {
        name: 'Date of Incident',
        selector: (row: any) => moment(row.dateOfIncident || row.date_of_incident || row.date).format('MM/DD/YY'),
      },
      {
        name: 'Policy Violation',
        selector: (row: any) => truncateCell(getPolicyViolationText(row) || '—', 80),
      },
      {
        name: 'Action',
        selector: (item: any) => renderViewButton('feedback', item),
      },
    ],
    'one-on-one': [
      {
        name: 'Date of Incident',
        selector: (row: any) => moment(row.dateOfIncident || row.date_of_incident || row.date).format('MM/DD/YY'),
      },
      {
        name: 'Policy Violation',
        selector: (row: any) => {
          const pv = getPolicyViolationText(row);
          const fallback =
            extractPerformanceField(row, ['meeting_goal', 'meetingGoal']) ||
            extractPerformanceField(row, ['description_of_one_on_one', 'descriptionOfOneOnOne']);
          return truncateCell(pv || fallback || '—', 80);
        },
      },
      {
        name: 'Action',
        selector: (item: any) => renderViewButton('one-on-one', item),
      },
    ],
    'performance-improvement': [
      {
        name: 'Date of Incident',
        selector: (row: any) => moment(row.dateOfIncident || row.date_of_incident || row.date).format('MM/DD/YY'),
      },
      {
        name: 'Policy Violation',
        selector: (row: any) => truncateCell(getPolicyViolationText(row) || '—', 80),
      },
      {
        name: 'Action',
        selector: (item: any) => renderViewButton('performance-improvement', item),
      },
    ],
    'verbal-warning': [
      {
        name: 'Date of Incident',
        selector: (row: any) => moment(row.dateOfIncident || row.date_of_incident || row.date).format('MM/DD/YY'),
      },
      {
        name: 'Policy Violation',
        selector: (row: any) => truncateCell(getPolicyViolationText(row) || '—', 80),
      },
      {
        name: 'Action',
        selector: (item: any) => renderViewButton('verbal-warning', item),
      },
    ],
    'disciplinary-action': [
      {
        name: 'Date of Incident',
        selector: (row: any) => moment(row.dateOfIncident || row.date_of_incident || row.date).format('MM/DD/YY'),
      },
      {
        name: 'Policy Violation',
        selector: (row: any) => truncateCell(getPolicyViolationText(row) || '—', 80),
      },
      {
        name: 'Action',
        selector: (item: any) => renderViewButton('disciplinary-action', item),
      },
    ],
  };

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (!userId) return;
      await loadPerformanceData();
      if (isMounted) {
        setInitialLoading(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  const loadPerformanceData = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const [
        feedbackResult,
        oneOnOneResult,
        performanceImprovementResult,
        verbalWarningResult,
        disciplinaryActionResult,
      ] = await Promise.all([
        PerformanceFeedback(token || '').getUserFeedbacks(userId, 'feedback'),
        PerformanceOneOnOne(token || '').getUserOneOnOnes(userId, 'one-on-one'),
        PerformanceImprovement(token || '').getUserPerformanceImprovements(userId, 'performance-improvement'),
        PerformanceVerbalWarning(token || '').getUserVerbalWarnings(userId, 'verbal-warning'),
        PerformanceDisciplinaryAction(token || '').getUserDisciplinaryActions(userId, 'disciplinary-action'),
      ]);

      setFeedbackData(feedbackResult?.data || []);
      setOneOnOneData(oneOnOneResult?.data || []);
      setPerformanceImprovementData(performanceImprovementResult?.data || []);
      setVerbalWarningData(verbalWarningResult?.data || []);
      setDisciplinaryActionData(disciplinaryActionResult?.data || []);
    } catch (error) {
      console.error('Error loading performance data:', error);
      toast.error('Error loading performance data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = (itemId: string) => {
    setActiveAccordion(activeAccordion === itemId ? null : itemId);
  };

  const getDataForType = (type: string) => {
    switch (type) {
      case 'feedback':
        return feedbackData;
      case 'one-on-one':
        return oneOnOneData;
      case 'performance-improvement':
        return performanceImprovementData;
      case 'verbal-warning':
        return verbalWarningData;
      case 'disciplinary-action':
        return disciplinaryActionData;
      default:
        return [];
    }
  };

  const getItemCount = (type: string) => {
    const data = getDataForType(type);
    return data.length;
  };

  const handleDownloadPdf = async (type: string, item: any) => {
    try {
      let url: string;
      let fileName: string;

      if (type === 'verbal-warning' && item?.signedDocumentUrl) {
        const response = await fetch(item.signedDocumentUrl);
        if (!response.ok) throw new Error('Failed to fetch signed document');
        const blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        fileName = 'Verbal_Warning_Signed.pdf';
      } else if (type === 'disciplinary-action' && item?.signedDocumentUrl) {
        const response = await fetch(item.signedDocumentUrl);
        if (!response.ok) throw new Error('Failed to fetch signed document');
        const blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        fileName = 'Disciplinary_Action_Signed.pdf';
      } else if (type === 'feedback') {
        ({ url, fileName } = await generateFeedbackPdf(item, user, currentSchool));
      } else if (type === 'one-on-one') {
        ({ url, fileName } = await generateOneOnOnePdf(
          { ...item, privateNotes: undefined, private_notes: undefined },
          user,
          currentSchool,
        ));
      } else if (type === 'performance-improvement' && item?.signedDocumentUrl) {
        const response = await fetch(item.signedDocumentUrl);
        if (!response.ok) throw new Error('Failed to fetch signed document');
        const blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        fileName = 'Performance_Improvement_Signed.pdf';
      } else if (type === 'performance-improvement') {
        ({ url, fileName } = await generateImprovementPdf({ ...item, privateNotes: undefined }, user, currentSchool));
      } else if (type === 'verbal-warning') {
        ({ url, fileName } = await generateVerbalWarningPdf(item, user, currentSchool));
      } else if (type === 'disciplinary-action') {
        ({ url, fileName } = await generateDisciplinaryActionPdf(item, user, currentSchool));
      } else {
        toast.info(
          'PDF export available only for feedback, one-on-one, performance improvement, verbal warning, and disciplinary action entries at this time.',
        );
        return;
      }

      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60_000);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Unable to generate the feedback PDF. Please try again later.');
    }
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setDetailModalType(null);
    setDetailModalItem(null);
  };

  const handleViewItem = (type: string, item: any) => {
    setDetailModalType(type);
    setDetailModalItem(item);
    setDetailModalOpen(true);
  };

  const renderViewButton = (type: string, item: any) => (
    <div className="d-flex justify-content-center">
      <Button
        outline
        color="info"
        size="sm"
        style={{ padding: '0.1rem 0.4rem', border: 'none' }}
        title="Download PDF"
        onClick={() => handleDownloadPdf(type, item)}
      >
        <i className="fas fa-download" />
      </Button>
      <Button
        outline
        color="info"
        size="sm"
        style={{ padding: '0.1rem 0.4rem', border: 'none' }}
        title="View details"
        onClick={() => handleViewItem(type, item)}
      >
        <i className="fas fa-eye" />
      </Button>
    </div>
  );

  const getColumnStyle = (columnName: string): any => {
    const policyViolationColumns = ['Policy Violation'];
    const dateColumns = ['Date of Incident'];
    const actionColumns = ['Action'];

    if (policyViolationColumns.includes(columnName)) {
      return {
        maxWidth: '300px',
        wordBreak: 'break-word',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        maxHeight: '100px',
        overflowY: 'auto',
      };
    }

    if (dateColumns.includes(columnName)) {
      return {
        width: '120px',
        minWidth: '120px',
        whiteSpace: 'nowrap',
      };
    }

    if (actionColumns.includes(columnName)) {
      return {
        width: '120px',
        minWidth: '120px',
        whiteSpace: 'nowrap',
      };
    }

    return {
      maxWidth: '200px',
      wordBreak: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal',
    };
  };

  const renderPerformanceList = (columns: any[], items: any[]) => {
    if (loading) {
      return (
        <div className="text-center py-3">
          <i className="fas fa-spinner fa-spin"></i> Loading...
        </div>
      );
    }

    if (!columns || !Array.isArray(columns)) {
      return (
        <Alert color="warning" className="mb-0">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          No columns configuration available for this category.
        </Alert>
      );
    }

    if (!items || !Array.isArray(items) || items.length === 0) {
      return (
        <Alert color="info" className="mb-0">
          <i className="fas fa-info-circle mr-2"></i>
          No items found for this category.
        </Alert>
      );
    }

    return (
      <div className="list-group list-group-flush" style={{ overflowX: 'auto' }}>
        <Table responsive style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.name} style={getColumnStyle(column.name)}>
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                {columns.map((column) => (
                  <td
                    key={column.name}
                    style={getColumnStyle(column.name)}
                    title={column.selector ? String(column.selector(item)) : String(item[column.name] || '')}
                  >
                    {column.selector ? column.selector(item) : item[column.name]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const formatModalDate = (value?: string) => {
    if (!value) {
      return '—';
    }
    const m = moment(value);
    return m.isValid() ? m.format('MM/DD/YY') : value;
  };

  const completedByLabel =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.name || user?.email || '—';

  const renderDetailModalBody = () => {
    if (!detailModalType || !detailModalItem) {
      return null;
    }
    const item = detailModalItem;
    const fieldBoxClass = 'border rounded p-3 mb-3 bg-light';

    if (detailModalType === 'feedback') {
      const employeeName =
        extractPerformanceField(item, ['name', 'employeeName', 'employee_name', 'employee.name']) || '—';
      const position = extractPerformanceField(item, ['position', 'employeePosition', 'jobTitle', 'job_title']) || '—';
      const program =
        extractPerformanceField(item, [
          'programName',
          'program_name',
          'program',
          'department',
          'department.name',
          'departmentName',
        ]) || '—';
      const dateInc = formatModalDate(
        extractPerformanceField(item, ['date_of_incident', 'dateOfIncident', 'date']) || item.dateOfIncident,
      );
      const policyViolation = getPolicyViolationText(item) || '—';
      const description =
        extractPerformanceField(item, ['description_of_feedback', 'descriptionOfFeedback', 'description']) || '—';
      const immediate = extractPerformanceField(item, ['immediate_feedback', 'immediateFeedback', 'immediate']) || '—';
      const expectation =
        extractPerformanceField(item, ['expectation_for_improvement', 'expectationForImprovement', 'expectation']) ||
        '—';
      const dateCompleted = formatModalDate(
        extractPerformanceField(item, ['created_at', 'createdAt']) || item.createdAt,
      );

      return (
        <>
          {(currentSchool as any)?.label ? <p className="text-muted mb-2">{(currentSchool as any).label}</p> : null}
          <p className="text-muted small mb-3">
            The purpose of this feedback is to address concerns regarding specific performance or behavior issues.
          </p>
          <Row className="mb-3">
            <Col md="6">
              <div className="text-muted small">Completed by</div>
              <div>{completedByLabel}</div>
            </Col>
            <Col md="6">
              <div className="text-muted small">Date Completed</div>
              <div>{dateCompleted}</div>
            </Col>
          </Row>
          <div className="border rounded p-3 mb-3">
            <Row>
              <Col md="6" className="mb-3">
                <div className="text-muted small">Name</div>
                <div>{employeeName}</div>
              </Col>
              <Col md="6" className="mb-3">
                <div className="text-muted small">Date of Incident</div>
                <div>{dateInc}</div>
              </Col>
              <Col md="6" className="mb-3 mb-md-0">
                <div className="text-muted small">Position</div>
                <div>{position}</div>
              </Col>
              <Col md="6">
                <div className="text-muted small">Program</div>
                <div>{program}</div>
              </Col>
            </Row>
          </div>
          <div className="mb-2 text-muted small font-weight-bold">Policy Violation</div>
          <div className={fieldBoxClass}>{policyViolation}</div>
          <div className="mb-2 text-muted small font-weight-bold">Description of Feedback</div>
          <div className={fieldBoxClass}>{description}</div>
          <div className="mb-2 text-muted small font-weight-bold">Immediate Feedback</div>
          <div className={fieldBoxClass}>{immediate}</div>
          <div className="mb-2 text-muted small font-weight-bold">Expectation for Improvement</div>
          <div className={fieldBoxClass}>{expectation}</div>
        </>
      );
    }

    const typeLabels: { [key: string]: { title: string; subtitle: string; typeValue: string } } = {
      'one-on-one': {
        title: 'One on One Details',
        subtitle: 'Detailed information about the One on One record.',
        typeValue: 'One on One',
      },
      'performance-improvement': {
        title: 'Performance Improvement Details',
        subtitle: 'Detailed information about the Performance Improvement record.',
        typeValue: 'Performance Improvement',
      },
      'verbal-warning': {
        title: 'Verbal Warning Details',
        subtitle: 'Detailed information about the Verbal Warning record.',
        typeValue: 'Verbal Warning',
      },
      'disciplinary-action': {
        title: 'Disciplinary Action Notice Details',
        subtitle: 'Detailed information about the Disciplinary Action Notice record.',
        typeValue: 'Disciplinary Action Notice',
      },
    };

    const meta = typeLabels[detailModalType];
    if (!meta) {
      return null;
    }

    const incidentDate = formatModalDate(
      extractPerformanceField(item, ['date_of_incident', 'dateOfIncident', 'date']) || item.date,
    );
    let policyViolation = getPolicyViolationText(item);
    if (detailModalType === 'one-on-one' && !policyViolation) {
      policyViolation =
        extractPerformanceField(item, ['meeting_goal', 'meetingGoal']) ||
        extractPerformanceField(item, ['description_of_one_on_one', 'descriptionOfOneOnOne']) ||
        '—';
    }
    if (!policyViolation) {
      policyViolation = '—';
    }

    let detailsText = '—';
    if (detailModalType === 'one-on-one') {
      detailsText =
        joinDetailParts([
          extractPerformanceField(item, ['description_of_one_on_one', 'descriptionOfOneOnOne']),
          extractPerformanceField(item, ['private_notes', 'privateNotes']),
        ]) || '—';
    } else if (detailModalType === 'performance-improvement') {
      detailsText =
        joinDetailParts([
          extractPerformanceField(item, [
            'reason_for_performance_improvement_plan',
            'reasonForPerformanceImprovementPlan',
            'reason',
          ]),
          extractPerformanceField(item, ['expectations_and_goals', 'expectationsAndGoals', 'expectations']),
          extractPerformanceField(item, ['timeline_and_deadline', 'timelineAndDeadline', 'timeline']),
          extractPerformanceField(item, ['resources_and_support', 'resourcesAndSupport', 'resources']),
        ]) || '—';
    } else if (detailModalType === 'verbal-warning') {
      detailsText =
        joinDetailParts([
          extractPerformanceField(item, ['reason_for_verbal_warning', 'reasonForVerbalWarning', 'reason']),
          extractPerformanceField(item, ['next_consequences', 'nextConsequences']),
          extractPerformanceField(item, ['expectation_of_improvement', 'expectationOfImprovement', 'expectations']),
          extractPerformanceField(item, ['private_notes', 'privateNotes']),
        ]) || '—';
    } else if (detailModalType === 'disciplinary-action') {
      detailsText =
        joinDetailParts([
          extractPerformanceField(item, ['reason_for_disciplinary_action', 'reasonForDisciplinaryAction', 'reason']),
          extractPerformanceField(item, ['action_taken', 'actionTaken', 'action']),
          extractPerformanceField(item, [
            'expectationOfImprovement',
            'expected_improvement',
            'expectedImprovement',
            'expectations',
          ]),
          extractPerformanceField(item, ['private_notes', 'privateNotes']),
        ]) || '—';
    }

    return (
      <>
        <p className="text-muted small mb-3">{meta.subtitle}</p>
        <div className="border rounded p-3">
          <Row className="mb-3">
            <Col md="6">
              <div className="text-muted small">Date</div>
              <div>{incidentDate}</div>
            </Col>
            <Col md="6">
              <div className="text-muted small">Type</div>
              <div>{meta.typeValue}</div>
            </Col>
          </Row>
          <div className="mb-2 text-muted small font-weight-bold">Policy Violation</div>
          <div className="border rounded p-3 mb-3 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
            {policyViolation}
          </div>
          <div className="mb-2 text-muted small font-weight-bold">Details</div>
          <div className="border rounded p-3 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
            {detailsText}
          </div>
        </div>
      </>
    );
  };

  const detailModalTitle =
    detailModalType === 'feedback'
      ? 'Feedback'
      : detailModalType === 'one-on-one'
      ? 'One on One Details'
      : detailModalType === 'performance-improvement'
      ? 'Performance Improvement Details'
      : detailModalType === 'verbal-warning'
      ? 'Verbal Warning Details'
      : detailModalType === 'disciplinary-action'
      ? 'Disciplinary Action Notice Details'
      : '';

  if (initialLoading) {
    return <OpacityLoaderSpinner />;
  }

  return (
    <div className="performance-management-tab">
      <AnnualReviewsSection userId={userId} />
      <Row>
        <Col>
          {accordionItems
            .filter((item) => item.enabled)
            .map((item) => (
              <Card key={item.id} className="mb-3">
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleAccordion(item.id)}
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <Row className="align-items-center">
                    <Col>
                      <div className="d-flex align-items-center">
                        <i className={`${item.icon} mr-2`}></i>
                        <span className="font-weight-bold">{item.title}</span>
                        <Badge color="primary" className="ml-2">
                          {getItemCount(item.id)}
                        </Badge>
                      </div>
                    </Col>
                    <Col className="text-right">
                      <i className={`fas fa-chevron-${activeAccordion === item.id ? 'up' : 'down'}`}></i>
                    </Col>
                  </Row>
                </CardHeader>
                <Collapse isOpen={activeAccordion === item.id}>
                  <CardBody>{renderPerformanceList(columnsByType[item.id] || [], getDataForType(item.id))}</CardBody>
                </Collapse>
              </Card>
            ))}
        </Col>
      </Row>

      <Modal isOpen={detailModalOpen} toggle={handleCloseDetailModal} size="lg">
        <ModalHeader toggle={handleCloseDetailModal}>{detailModalTitle}</ModalHeader>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>{renderDetailModalBody()}</ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={handleCloseDetailModal}>
            Close
          </Button>
          <Button
            color="dark"
            disabled={!detailModalType || !detailModalItem}
            onClick={() => {
              if (detailModalType && detailModalItem) {
                handleDownloadPdf(detailModalType, detailModalItem);
              }
            }}
          >
            <i className="fas fa-download mr-2" />
            Download PDF
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PerformanceManagementTab;
